import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { X } from "lucide-react";
import { api } from "../lib/api";

const DISMISS_KEY = "endag1_announcement_dismissed_v";

export default function AnnouncementBanner() {
  const [ann, setAnn] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/announcement")
      .then(({ data }) => { if (!cancelled) setAnn(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Dismiss key includes message so a new announcement re-appears for repeat users
  const key = ann?.message ? DISMISS_KEY + btoa(unescape(encodeURIComponent(ann.message))).slice(0, 24) : null;
  useEffect(() => {
    if (key && localStorage.getItem(key)) setDismissed(true);
    else setDismissed(false);
  }, [key]);

  const visible = !!(ann && ann.enabled && ann.message && !dismissed);

  // Publish banner height as a CSS variable so the fixed navbar can shift down
  useLayoutEffect(() => {
    const setVar = () => {
      const h = visible && ref.current ? ref.current.offsetHeight : 0;
      document.documentElement.style.setProperty("--announcement-h", `${h}px`);
    };
    setVar();
    window.addEventListener("resize", setVar);
    return () => {
      window.removeEventListener("resize", setVar);
      document.documentElement.style.setProperty("--announcement-h", "0px");
    };
  }, [visible, ann]);

  if (!visible) return null;

  const dismiss = () => {
    if (key) localStorage.setItem(key, "1");
    setDismissed(true);
  };

  const bg = ann.bg_color || "#4ADE80";
  const fg = ann.text_color || "#0F0F10";

  return (
    <div
      ref={ref}
      data-testid="announcement-banner"
      style={{ background: bg, color: fg }}
      className="fixed top-0 left-0 right-0 z-[60] text-center text-[12px] sm:text-[13px] font-medium tracking-[0.02em] py-2.5 px-10"
    >
      <span data-testid="announcement-message">{ann.message}</span>
      {ann.link_url && ann.link_label && (
        <a
          data-testid="announcement-link"
          href={ann.link_url}
          className="ml-3 underline underline-offset-4 hover:opacity-80 transition-opacity"
        >
          {ann.link_label} →
        </a>
      )}
      <button
        data-testid="announcement-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity"
        style={{ color: fg }}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
